"use client";

import { memo, useMemo } from "react";
import { cn } from "../../../lib";
import {
	buildPathConnectorSegments,
	type PathConnectorCellInput,
} from "../lib/build-path-connector-segments";
import type { HexagonLayoutMetrics } from "../lib/hexagon-layout-metrics";

export interface HexagonPathConnectorLayerProps {
	cells: readonly PathConnectorCellInput[];
	metrics: HexagonLayoutMetrics;
	contentWidth: number;
	contentHeight: number;
	borderWidthPx: number;
	activePlayerCoord: { row: number; col: number } | null;
	isPathIndexVisible: (pathIndex: number) => boolean;
	lockedPathCoordKeys?: ReadonlySet<string>;
	/** Spotlight route keys — when set with layerPass, filters segments. */
	scopedPanelSpotlightRenderCoordKeys?: ReadonlySet<string>;
	layerPass?: "full" | "below-dim" | "above-dim";
	reducedMotion?: boolean;
	className?: string;
}

function segmentInLayerPass(
	pass: "full" | "below-dim" | "above-dim",
	fromKey: string,
	toKey: string,
	spotlightKeys: ReadonlySet<string> | undefined,
): boolean {
	if (pass === "full" || !spotlightKeys) {
		return true;
	}
	const fromSpot = spotlightKeys.has(fromKey);
	const toSpot = spotlightKeys.has(toKey);
	const eitherSpot = fromSpot || toSpot;
	if (pass === "above-dim") {
		return eitherSpot;
	}
	return !eitherSpot;
}

/**
 * Center-to-center path connectors behind path tiles.
 * Stroke width/color match the earlier tile’s path border; draw-in follows
 * the same pathIndex reveal gate as path tiles.
 */
export const HexagonPathConnectorLayer = memo(
	function HexagonPathConnectorLayer({
		cells,
		metrics,
		contentWidth,
		contentHeight,
		borderWidthPx,
		activePlayerCoord,
		isPathIndexVisible,
		lockedPathCoordKeys,
		scopedPanelSpotlightRenderCoordKeys,
		layerPass = "full",
		reducedMotion = false,
		className,
	}: HexagonPathConnectorLayerProps) {
		const segments = useMemo(
			() =>
				buildPathConnectorSegments({
					cells,
					metrics,
					activePlayerCoord,
					lockedPathCoordKeys,
				}),
			[activePlayerCoord, cells, lockedPathCoordKeys, metrics],
		);

		if (contentWidth <= 0 || contentHeight <= 0 || segments.length === 0) {
			return null;
		}

		return (
			<svg
				className={cn(
					"pointer-events-none absolute left-0 top-0 z-[5] overflow-visible",
					className,
				)}
				width={contentWidth}
				height={contentHeight}
				viewBox={`0 0 ${contentWidth} ${contentHeight}`}
				aria-hidden="true"
				data-hexagon-path-connectors=""
			>
				{segments.map((segment) => {
					if (
						!segmentInLayerPass(
							layerPass,
							segment.fromKey,
							segment.toKey,
							scopedPanelSpotlightRenderCoordKeys,
						)
					) {
						return null;
					}
					const visible = isPathIndexVisible(segment.toPathIndex);
					return (
						<line
							key={segment.key}
							className="sq-hex-path-connector-line"
							x1={segment.x1}
							y1={segment.y1}
							x2={segment.x2}
							y2={segment.y2}
							pathLength={1}
							stroke={segment.strokeColor}
							strokeWidth={borderWidthPx}
							strokeLinecap="round"
							style={{
								["--sq-path-connector-opacity" as string]:
									segment.opacity,
							}}
							data-visible={visible ? "true" : "false"}
							data-reduced-motion={
								reducedMotion ? "true" : "false"
							}
						/>
					);
				})}
			</svg>
		);
	},
);
